import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Collapse,
    Divider,
    Alert,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as BackIcon,
    DragIndicator as DragIcon,
    VideoLibrary as VideoIcon,
    Description as DocumentIcon,
    Quiz as QuizIcon,
    TextFields as TextIcon,
    Code as CodeIcon,
    Article as ContentIcon,
    SwapHoriz as MatchingIcon,
    Sort as SortingIcon,
    TouchApp as HotspotIcon,
    Timeline as TimelineIcon,
    Visibility as PreviewIcon,
    Info as InfoIcon,
    RateReview as PeerReviewIcon,
    LibraryBooks as LibraryIcon,
    LibraryAdd as LibraryAddIcon,
    SmartToy as TutorIcon,
    People as PeopleIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, Title, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { SaveToLibraryDialog, SelectFromLibraryDialog } from '../Library';
import CourseEnrollmentDialog from './CourseEnrollmentDialog';
import { clampMinPeerReviews } from './peerReviewFormUtils';

interface Lesson {
    id: number;
    title: string;
    type: string;
    description?: string;
    sortOrder: number;
    estimatedDuration?: number;
    isActive: boolean;
    isMandatory: boolean;
    minScore?: number;
    content?: any;
    hasPeerReview?: boolean;
    minPeerReviews?: number;
    peerReviewCriteria?: PeerReviewCriterion[];
    aiTutorEnabled?: boolean | null; // null = inherit from course
}

interface PeerReviewCriterion {
    id: string;
    name: string;
    description: string;
    maxPoints: number;
}

interface Course {
    id: number;
    title: string;
}


const CourseLessons: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const [course, setCourse] = useState<Course | null>(null);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        type: 'video',
        description: '',
        estimatedDuration: 0,
        isActive: true,
        isMandatory: false,
        minScore: 0,
        hasPeerReview: false,
        minPeerReviews: 2,
        peerReviewCriteria: [] as PeerReviewCriterion[],
        aiTutorEnabled: null as boolean | null // null = inherit from course
    });
    const [saveLibraryDialogOpen, setSaveLibraryDialogOpen] = useState(false);
    const [lessonToSave, setLessonToSave] = useState<Lesson | null>(null);
    const [selectFromLibraryOpen, setSelectFromLibraryOpen] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const translate = useTranslate();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = lessons.findIndex(l => l.id === active.id);
        const newIndex = lessons.findIndex(l => l.id === over.id);
        const reordered = arrayMove(lessons, oldIndex, newIndex).map((l, i) => ({ ...l, sortOrder: i }));
        setLessons(reordered);

        setReordering(true);
        const originalSortOrders = new Map(lessons.map(l => [l.id, l.sortOrder]));
        try {
            await Promise.all(
                reordered
                    .filter(l => l.sortOrder !== originalSortOrders.get(l.id))
                    .map(l => dataProvider.update('lessons', { id: l.id, data: { sortOrder: l.sortOrder }, previousData: l }))
            );
        } catch {
            notify('Fehler beim Speichern der Reihenfolge', { type: 'error' });
            loadLessons();
        } finally {
            setReordering(false);
        }
    };

    const lessonTypes = [
        { value: 'video', label: translate('lessons.types.video'), icon: VideoIcon },
        { value: 'document', label: translate('lessons.types.document'), icon: DocumentIcon },
        { value: 'info_text', label: translate('lessons.types.infoText'), icon: InfoIcon },
        { value: 'multiple_choice', label: translate('lessons.types.multipleChoice'), icon: QuizIcon },
        { value: 'fill_in_blank', label: translate('lessons.types.fillInBlank'), icon: TextIcon },
        { value: 'free_text', label: translate('lessons.types.freeText'), icon: TextIcon },
        { value: 'drag_drop', label: translate('lessons.types.dragDrop'), icon: DragIcon },
        { value: 'matching', label: translate('lessons.types.matching'), icon: MatchingIcon },
        { value: 'sorting', label: translate('lessons.types.sorting'), icon: SortingIcon },
        { value: 'hotspot', label: translate('lessons.types.hotspot'), icon: HotspotIcon },
        { value: 'timeline', label: translate('lessons.types.timeline'), icon: TimelineIcon },
        { value: 'scorm', label: translate('lessons.types.scorm'), icon: CodeIcon },
        { value: 'h5p', label: translate('lessons.types.h5p'), icon: CodeIcon },
        { value: 'xapi', label: translate('lessons.types.xapi'), icon: CodeIcon }
    ];

    useEffect(() => {
        if (courseId) {
            loadCourse();
            loadLessons();
        }
    }, [courseId]);

    const loadCourse = async () => {
        try {
            const { data } = await dataProvider.getOne('courses', { id: parseInt(courseId!) });
            setCourse(data as Course);
        } catch (error) {
            notify(translate('lessons.loadCourseError'), { type: 'error' });
        }
    };

    const loadLessons = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('lessons', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'sortOrder', order: 'ASC' },
                filter: { 'course.id': parseInt(courseId!) }
            });
            setLessons(data as Lesson[]);
        } catch (error) {
            notify(translate('lessons.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (lesson?: Lesson) => {
        if (lesson) {
            setEditingLesson(lesson);
            setFormData({
                title: lesson.title,
                type: lesson.type,
                description: lesson.description || '',
                estimatedDuration: lesson.estimatedDuration || 0,
                isActive: lesson.isActive,
                isMandatory: lesson.isMandatory,
                minScore: lesson.minScore || 0,
                hasPeerReview: lesson.hasPeerReview || false,
                minPeerReviews: lesson.minPeerReviews ?? 2,
                peerReviewCriteria: lesson.peerReviewCriteria || [],
                aiTutorEnabled: lesson.aiTutorEnabled ?? null
            });
        } else {
            setEditingLesson(null);
            setFormData({
                title: '',
                type: 'video',
                description: '',
                estimatedDuration: 0,
                isActive: true,
                isMandatory: false,
                minScore: 0,
                hasPeerReview: false,
                minPeerReviews: 2,
                peerReviewCriteria: [],
                aiTutorEnabled: null
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingLesson(null);
    };

    const handleSave = async () => {
        try {
            const lessonData = {
                ...formData,
                course: `/api/courses/${courseId}`,
                sortOrder: editingLesson ? editingLesson.sortOrder : lessons.length
            };

            if (editingLesson) {
                await dataProvider.update('lessons', {
                    id: editingLesson.id,
                    data: lessonData,
                    previousData: editingLesson
                });
                notify(translate('lessons.updateSuccess'), { type: 'success' });
            } else {
                await dataProvider.create('lessons', {
                    data: lessonData
                });
                notify(translate('lessons.createSuccess'), { type: 'success' });
            }
            handleCloseDialog();
            loadLessons();
        } catch (error) {
            notify(translate('lessons.saveError'), { type: 'error' });
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(translate('lessons.confirmDelete'))) {
            return;
        }

        try {
            await dataProvider.delete('lessons', {
                id,
                previousData: {} as any
            });
            notify(translate('lessons.deleteSuccess'), { type: 'success' });
            loadLessons();
        } catch (error: any) {
            const detail = error?.body?.['hydra:description'] || error?.message || '';
            notify(detail ? `${translate('lessons.deleteError')}: ${detail}` : translate('lessons.deleteError'), { type: 'error' });
        }
    };

    const getLessonTypeLabel = (type: string) => {
        const lessonType = lessonTypes.find(t => t.value === type);
        return lessonType ? lessonType.label : type;
    };

    const getLessonTypeIcon = (type: string) => {
        const lessonType = lessonTypes.find(t => t.value === type);
        return lessonType ? lessonType.icon : DocumentIcon;
    };

    const requiresScore = (type: string) => {
        return ['multiple_choice', 'fill_in_blank', 'free_text', 'drag_drop', 'matching', 'sorting', 'hotspot', 'timeline', 'scorm', 'h5p', 'xapi'].includes(type);
    };

    const handleSaveToLibrary = (lesson: Lesson) => {
        setLessonToSave(lesson);
        setSaveLibraryDialogOpen(true);
    };

    const SortableLesson: React.FC<{ lesson: Lesson; index: number }> = ({ lesson, index }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });
        const Icon = getLessonTypeIcon(lesson.type);

        return (
            <React.Fragment>
                <ListItem
                    ref={setNodeRef}
                    style={{
                        transform: CSS.Transform.toString(transform),
                        transition,
                        opacity: isDragging ? 0.5 : 1,
                        backgroundColor: isDragging ? 'action.hover' : undefined,
                        zIndex: isDragging ? 1 : undefined,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', '&:active': { cursor: 'grabbing' } }}>
                            <DragIcon color="action" />
                        </Box>
                        <Icon color="action" />
                        <Box sx={{ flex: 1 }}>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle1">
                                            {index + 1}. {lesson.title}
                                        </Typography>
                                        <Chip label={getLessonTypeLabel(lesson.type)} size="small" variant="outlined" />
                                        {lesson.isMandatory && <Chip label={translate('lessons.mandatory')} size="small" color="error" />}
                                        {!lesson.isActive && <Chip label={translate('catalogs.inactive')} size="small" color="default" />}
                                        {lesson.hasPeerReview && (
                                            <Chip
                                                icon={<PeerReviewIcon sx={{ fontSize: 16 }} />}
                                                label={translate('lessons.peerReview')}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        {lesson.description && (
                                            <Typography variant="body2" color="text.secondary">{lesson.description}</Typography>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                            {lesson.estimatedDuration && (
                                                <Typography variant="caption" color="text.secondary">
                                                    ⏱ {translate('courses.duration', { minutes: lesson.estimatedDuration })}
                                                </Typography>
                                            )}
                                            {lesson.minScore && (
                                                <Typography variant="caption" color="text.secondary">
                                                    🎯 {translate('lessons.minScore', { score: lesson.minScore })}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                }
                            />
                        </Box>
                        <ListItemSecondaryAction>
                            <Tooltip title={translate('library.saveDialog.title')}>
                                <IconButton edge="end" onClick={() => handleSaveToLibrary(lesson)}>
                                    <LibraryAddIcon />
                                </IconButton>
                            </Tooltip>
                            <IconButton edge="end" onClick={() => navigate(`/lesson/${lesson.id}/content`)} title={translate('lessons.editContent')}>
                                <ContentIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => handleOpenDialog(lesson)}>
                                <EditIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => handleDelete(lesson.id)}>
                                <DeleteIcon />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </Box>
                </ListItem>
                {index < lessons.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
            </React.Fragment>
        );
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>{translate('lessons.loading')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Title title={`${translate('lessons.title')} - ${course?.title || translate('courses.title')}`} />

            <Box sx={{ mb: 3 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate(-1)}>
                    {translate('lessons.backToCourses')}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{translate('lessons.lessonsFor', { title: course?.title })}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<PeopleIcon />}
                        onClick={() => setEnrollmentDialogOpen(true)}
                    >
                        {translate('courseEnrollmentDialog.title')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={() => navigate(`/course-player/${courseId}?preview=true`)}
                    >
                        {translate('lessons.preview')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<LibraryIcon />}
                        onClick={() => setSelectFromLibraryOpen(true)}
                    >
                        {translate('lessons.fromLibrary', 'Aus Bibliothek')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {translate('lessons.newLesson')}
                    </Button>
                </Box>
            </Box>

            {lessons.length > 0 ? (
                <Card>
                    {reordering && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'action.hover' }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">Reihenfolge wird gespeichert...</Typography>
                        </Box>
                    )}
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={lessons.map(l => l.id)} strategy={verticalListSortingStrategy}>
                            <List>
                                {lessons.map((lesson, index) => (
                                    <SortableLesson key={lesson.id} lesson={lesson} index={index} />
                                ))}
                            </List>
                        </SortableContext>
                    </DndContext>
                </Card>
            ) : (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                        {translate('lessons.noLessons')}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 2 }}
                    >
                        {translate('lessons.createFirst')}
                    </Button>
                </Box>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingLesson ? translate('lessons.editLesson') : translate('lessons.newLesson')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label={translate('lessons.form.title')}
                            fullWidth
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />

                        <FormControl fullWidth required>
                            <InputLabel>{translate('lessons.form.type')}</InputLabel>
                            <Select
                                value={formData.type}
                                label={translate('lessons.form.type')}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            >
                                {lessonTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label={translate('lessons.form.description')}
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />

                        <TextField
                            label={translate('lessons.form.estimatedDuration')}
                            type="number"
                            fullWidth
                            value={formData.estimatedDuration}
                            onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 0 })}
                        />

                        {requiresScore(formData.type) && (
                            <TextField
                                label={translate('lessons.form.minScore')}
                                type="number"
                                fullWidth
                                value={formData.minScore}
                                onChange={(e) => setFormData({ ...formData, minScore: parseInt(e.target.value) || 0 })}
                                helperText={translate('lessons.form.minScoreHelp')}
                            />
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isMandatory}
                                    onChange={(e) => setFormData({ ...formData, isMandatory: e.target.checked })}
                                />
                            }
                            label={translate('lessons.form.isMandatory')}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            }
                            label={translate('lessons.form.active')}
                        />

                        <Divider sx={{ my: 2 }} />

                        {/* Peer Review Configuration */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.hasPeerReview}
                                    onChange={(e) => setFormData({ ...formData, hasPeerReview: e.target.checked })}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PeerReviewIcon fontSize="small" />
                                    {translate('lessons.form.enablePeerReview')}
                                </Box>
                            }
                        />

                        <Collapse in={formData.hasPeerReview}>
                            <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    {translate('lessons.form.peerReviewInfo')}
                                </Alert>

                                <TextField
                                    label={translate('lessons.form.minPeerReviews')}
                                    type="number"
                                    size="small"
                                    value={formData.minPeerReviews}
                                    onChange={(e) => {
                                        const clamped = clampMinPeerReviews(parseInt(e.target.value, 10));
                                        setFormData({ ...formData, minPeerReviews: clamped });
                                    }}
                                    inputProps={{ min: 1, max: 10 }}
                                    helperText={translate('lessons.form.minPeerReviewsHelp')}
                                    sx={{ width: 240 }}
                                />

                                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                    {translate('lessons.form.peerReviewCriteria')}
                                </Typography>

                                {formData.peerReviewCriteria.map((criterion, index) => (
                                    <Box key={criterion.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <TextField
                                            label={translate('lessons.form.criterionName')}
                                            size="small"
                                            value={criterion.name}
                                            onChange={(e) => {
                                                const updated = [...formData.peerReviewCriteria];
                                                updated[index] = { ...criterion, name: e.target.value };
                                                setFormData({ ...formData, peerReviewCriteria: updated });
                                            }}
                                            sx={{ flex: 1 }}
                                        />
                                        <TextField
                                            label={translate('lessons.form.criterionDescription')}
                                            size="small"
                                            value={criterion.description}
                                            onChange={(e) => {
                                                const updated = [...formData.peerReviewCriteria];
                                                updated[index] = { ...criterion, description: e.target.value };
                                                setFormData({ ...formData, peerReviewCriteria: updated });
                                            }}
                                            sx={{ flex: 2 }}
                                        />
                                        <TextField
                                            label={translate('lessons.form.maxPoints')}
                                            type="number"
                                            size="small"
                                            value={criterion.maxPoints}
                                            onChange={(e) => {
                                                const updated = [...formData.peerReviewCriteria];
                                                updated[index] = { ...criterion, maxPoints: parseInt(e.target.value) || 5 };
                                                setFormData({ ...formData, peerReviewCriteria: updated });
                                            }}
                                            inputProps={{ min: 1, max: 10 }}
                                            sx={{ width: 100 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const updated = formData.peerReviewCriteria.filter((_, i) => i !== index);
                                                setFormData({ ...formData, peerReviewCriteria: updated });
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ))}

                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const newCriterion: PeerReviewCriterion = {
                                            id: `criterion_${Date.now()}`,
                                            name: '',
                                            description: '',
                                            maxPoints: 5
                                        };
                                        setFormData({
                                            ...formData,
                                            peerReviewCriteria: [...formData.peerReviewCriteria, newCriterion]
                                        });
                                    }}
                                    variant="outlined"
                                    size="small"
                                >
                                    {translate('lessons.form.addCriterion')}
                                </Button>
                            </Box>
                        </Collapse>

                        <Divider sx={{ my: 2 }} />

                        {/* AI Tutor Configuration */}
                        <FormControl fullWidth>
                            <InputLabel>{translate('lessons.form.aiTutor', 'KI-Lernassistent')}</InputLabel>
                            <Select
                                value={formData.aiTutorEnabled === null ? 'inherit' : formData.aiTutorEnabled ? 'enabled' : 'disabled'}
                                label={translate('lessons.form.aiTutor', 'KI-Lernassistent')}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormData({
                                        ...formData,
                                        aiTutorEnabled: value === 'inherit' ? null : value === 'enabled'
                                    });
                                }}
                                startAdornment={<TutorIcon sx={{ mr: 1, color: 'action.active' }} />}
                            >
                                <MenuItem value="inherit">
                                    {translate('lessons.form.aiTutorInherit', 'Vom Kurs übernehmen')}
                                </MenuItem>
                                <MenuItem value="enabled">
                                    {translate('lessons.form.aiTutorEnabled', 'Aktiviert')}
                                </MenuItem>
                                <MenuItem value="disabled">
                                    {translate('lessons.form.aiTutorDisabled', 'Deaktiviert')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{translate('ra.action.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.title.trim()}>
                        {translate('ra.action.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save to Library Dialog */}
            {lessonToSave && (
                <SaveToLibraryDialog
                    open={saveLibraryDialogOpen}
                    onClose={() => {
                        setSaveLibraryDialogOpen(false);
                        setLessonToSave(null);
                    }}
                    contentType="lesson"
                    contentId={lessonToSave.id}
                    contentTitle={lessonToSave.title}
                    contentDescription={lessonToSave.description}
                />
            )}

            {/* Select from Library Dialog */}
            <SelectFromLibraryDialog
                open={selectFromLibraryOpen}
                onClose={() => setSelectFromLibraryOpen(false)}
                itemType="lesson"
                targetId={parseInt(courseId!)}
                onInserted={loadLessons}
            />

            {/* Course Enrollment Dialog */}
            {course && (
                <CourseEnrollmentDialog
                    open={enrollmentDialogOpen}
                    onClose={() => setEnrollmentDialogOpen(false)}
                    courseId={course.id}
                    courseTitle={course.title}
                />
            )}
        </Box>
    );
};

export default CourseLessons;
